import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useUser } from '../../UserContext';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Select,
    MenuItem,
    Button,
    Grid,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    CircularProgress,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControl,
    InputLabel
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

const GroupReports = () => {
    const { currentUser } = useUser();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [teachers, setTeachers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [selectedTeacher, setSelectedTeacher] = useState('');
    const [selectedGroup, setSelectedGroup] = useState('');
    const [groupType, setGroupType] = useState('all');
    const [gradeLevel, setGradeLevel] = useState('');
    const [teacherGradeLevels, setTeacherGradeLevels] = useState([]);
    const [teacherReport, setTeacherReport] = useState(null);
    const [groupReport, setGroupReport] = useState(null);
    const [reportKey, setReportKey] = useState(0); // Force re-render
    const [allGroupsReport, setAllGroupsReport] = useState(null);

    const gradeLevels = [
        { value: 1, label: 'Grade 6' },
        { value: 2, label: 'Grade 7' },
        { value: 3, label: 'Grade 8' },
        { value: 4, label: 'Grade 9' },
        { value: 5, label: 'Grade 10' },
        { value: 6, label: 'Grade 11' },
        { value: 7, label: 'Grade 12' }
    ];

    useEffect(() => {
        fetchTeachers();
        fetchAllGroups();
        
        // Set current teacher as default if logged in as teacher
        if (currentUser?.is_teacher && currentUser?.teacher_id) {
            setSelectedTeacher(currentUser.teacher_id.toString());
        }
    }, [currentUser]);

    // Fetch teacher grade levels when teacher changes
    useEffect(() => {
        if (selectedTeacher) {
            fetchTeacherGradeLevels(selectedTeacher);
        } else {
            setTeacherGradeLevels([]);
            setGradeLevel('');
        }
    }, [selectedTeacher]);

    // Fetch teacher groups when teacher or grade level changes
    useEffect(() => {
        if (selectedTeacher) {
            fetchTeacherGroups();
        } else {
            fetchAllGroups();
        }
    }, [selectedTeacher, gradeLevel]);

    const fetchTeachers = async () => {
        try {
            const response = await axios.get('/api/teachers/');
            setTeachers(response.data);
        } catch (err) {
            console.error('Error fetching teachers:', err);
        }
    };

    const fetchTeacherGradeLevels = async (teacherId) => {
        try {
            const response = await axios.get(`/api/teachers/${teacherId}/`);
            const teacher = response.data;
            // Extract grade levels from the teacher object
            const grades = teacher.assigned_grades || []; // This returns the actual grade numbers (6, 7, 8, etc.)
            setTeacherGradeLevels(grades);
            
            // Set grade level filter to only show teacher's grades if they have any
            if (grades.length > 0) {
                // Convert grade number to database ID (grade 6 = ID 1, grade 7 = ID 2, etc.)
                const gradeId = grades[0] - 5; // Grade 6 -> ID 1, Grade 7 -> ID 2, etc.
                setGradeLevel(gradeId.toString());
            }
        } catch (err) {
            console.error('Error fetching teacher grade levels:', err);
            setTeacherGradeLevels([]);
        }
    };

    const fetchAllGroups = async () => {
        try {
            const response = await axios.get('/api/groups/report/all-groups/');
            setGroups(response.data.groups || []);
        } catch (err) {
            console.error('Error fetching groups:', err);
        }
    };

    const fetchTeacherGroups = async () => {
        if (!selectedTeacher) {
            setError('Please select a teacher');
            return;
        }

        setLoading(true);
        setError('');
        try {
            let url = `/api/groups/report/teacher-groups/?teacher_id=${selectedTeacher}`;
            // Add grade level filter if specified
            if (gradeLevel) {
                url += `&grade_level=${gradeLevel}`;
            }
            const response = await axios.get(url);
            setTeacherReport(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load teacher groups report');
        } finally {
            setLoading(false);
        }
    };

    const fetchGroupStudents = async () => {
        if (!selectedGroup) {
            setError('Please select a group');
            return;
        }

        setLoading(true);
        setError('');
        
        try {
            // Find the selected group to get its type
            const selectedGroupData = groups.find(g => g.id === parseInt(selectedGroup));
            const groupType = selectedGroupData?.type || '';
            
            console.log('=== FETCH START ===');
            console.log('1. Selected Group ID:', selectedGroup);
            console.log('2. Selected Group Data from dropdown:', selectedGroupData);
            console.log('3. Group Type extracted:', groupType);
            console.log('4. API URL:', `/api/groups/report/group-students/${selectedGroup}/?type=${groupType}`);
            
            const response = await axios.get(`/api/groups/report/group-students/${selectedGroup}/?type=${groupType}`);
            
            console.log('5. Raw API Response:', response.data);
            console.log('6. Response Group Name:', response.data.group?.name);
            console.log('7. Response Group Type:', response.data.group?.type);
            console.log('8. Response Student Count:', response.data.students?.length);
            console.log('=== FETCH END ===');
            
            // Force re-render by incrementing key and setting new data
            setReportKey(prev => prev + 1);
            setGroupReport(response.data);
            
        } catch (err) {
            console.error('Error fetching group students:', err);
            setError(err.response?.data?.error || 'Failed to load group students report');
            setGroupReport(null);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllGroupsReport = async () => {
        setLoading(true);
        setError('');
        try {
            let url = '/api/groups/report/all-groups/';
            const params = new URLSearchParams();
            if (groupType !== 'all') params.append('type', groupType);
            if (gradeLevel) params.append('grade_level', gradeLevel);
            if (params.toString()) url += '?' + params.toString();

            const response = await axios.get(url);
            setAllGroupsReport(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to load all groups report');
        } finally {
            setLoading(false);
        }
    };

    const renderTeacherReport = () => {
        if (!teacherReport) return null;

        return (
            <Card sx={{ mt: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Teacher: {teacherReport.teacher.name}
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                Email: {teacherReport.teacher.email}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                Type: {teacherReport.teacher.teacher_type}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                Total Groups: {teacherReport.summary.total_groups}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                Total Students: {teacherReport.summary.total_students}
                            </Typography>
                        </Grid>
                    </Grid>

                    {teacherReport.groups.map((group, index) => (
                        <Accordion key={index}>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                                    <Typography variant="subtitle1">{group.name}</Typography>
                                    <Chip label={group.type.toUpperCase()} size="small" />
                                    <Typography variant="body2" color="text.secondary">
                                        {group.student_count} students
                                    </Typography>
                                </Box>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Grade Levels: {group.grade_levels.join(', ')}
                                </Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Student Name</TableCell>
                                                <TableCell>Grade Level</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {group.students.map((student, idx) => (
                                                <TableRow key={idx}>
                                                    <TableCell>
                                                        {student.first_name} {student.last_name}
                                                    </TableCell>
                                                    <TableCell>{student.grade_level}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </CardContent>
            </Card>
        );
    };

    // Separate component to force clean rendering
const GroupReportDisplay = ({ reportData, reportKey, selectedGroupId }) => {
    if (!reportData) return null;

    console.log('=== RENDER START ===');
    console.log('A. ReportData received:', reportData);
    console.log('B. ReportData.group:', reportData.group);
    console.log('C. ReportData.group.name:', reportData.group?.name);
    console.log('D. ReportData.group.type:', reportData.group?.type);
    console.log('E. ReportData.students:', reportData.students);
    console.log('F. ReportKey:', reportKey);
    console.log('G. SelectedGroupId:', selectedGroupId);
    console.log('H. ReportData.group.id:', reportData.group?.id);
    console.log('=== RENDER END ===');

    // Only render if the report data matches the selected group
    if (reportData.group?.id !== parseInt(selectedGroupId)) {
        console.log('ID MISMATCH - not rendering');
        return null;
    }

    return (
        <Card key={reportKey} sx={{ mt: 2 }}>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    Group: {reportData.group.name}
                </Typography>
                <Grid container spacing={2} sx={{ mb: 2 }}>
                    <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">
                            Type: {reportData.group.type.toUpperCase()}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">
                            Grade Levels: {reportData.group.grade_levels.join(', ')}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">
                            Total Students: {reportData.summary.total_students}
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                        <Typography variant="body2" color="text.secondary">
                            Active Students: {reportData.summary.active_students}
                        </Typography>
                    </Grid>
                </Grid>

                <TableContainer component={Paper} variant="outlined">
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Student Name</TableCell>
                                <TableCell>Grade Level</TableCell>
                                <TableCell>Email</TableCell>
                                <TableCell>Status</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reportData.students.map((student) => (
                                <TableRow key={`${reportKey}-${student.id}`}>
                                    <TableCell>
                                        {student.first_name} {student.last_name}
                                    </TableCell>
                                    <TableCell>{student.grade_level}</TableCell>
                                    <TableCell>{student.email || 'N/A'}</TableCell>
                                    <TableCell>
                                        <Chip 
                                            label={student.is_active ? 'Active' : 'Inactive'}
                                            color={student.is_active ? 'success' : 'error'}
                                            size="small"
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};

    const renderGroupReport = () => {
        return <GroupReportDisplay reportData={groupReport} reportKey={reportKey} selectedGroupId={selectedGroup} />;
    };

    const handleGroupChange = (event) => {
        const value = event.target.value;
        console.log('=== GROUP CHANGE START ===');
        console.log('1. Dropdown value selected:', value);
        console.log('2. Current selectedGroup state:', selectedGroup);
        console.log('3. Groups available:', groups.map(g => ({ id: g.id, name: g.name })));
        
        setSelectedGroup(value);
        
        console.log('4. Setting selectedGroup to:', value);
        console.log('=== GROUP CHANGE END ===');
    };

    // Helper function to get group display name
    const getGroupDisplayName = (groupId) => {
        if (!groupId) return '';
        const group = groups.find(g => g.id === parseInt(groupId));
        return group ? group.name : '';
    };

    const renderAllGroupsReport = () => {
        if (!allGroupsReport) return null;

        return (
            <Card sx={{ mt: 2 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        All Groups Summary
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                Total Groups: {allGroupsReport.summary.total_groups}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Typography variant="body2" color="text.secondary">
                                Total Students: {allGroupsReport.summary.total_students}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="body2" color="text.secondary">
                                By Type - Homeroom: {allGroupsReport.summary.by_type.homeroom}, 
                                CTE: {allGroupsReport.summary.by_type.cte}, 
                                Core: {allGroupsReport.summary.by_type.core}
                            </Typography>
                        </Grid>
                    </Grid>

                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Group Name</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Grade Levels</TableCell>
                                    <TableCell>Teacher</TableCell>
                                    <TableCell>Students</TableCell>
                                    <TableCell>Details</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {allGroupsReport.groups.map((group, index) => (
                                    <TableRow key={index}>
                                        <TableCell>{group.name}</TableCell>
                                        <TableCell>
                                            <Chip label={group.type.toUpperCase()} size="small" />
                                        </TableCell>
                                        <TableCell>{group.grade_levels.join(', ')}</TableCell>
                                        <TableCell>{group.teacher || 'Unassigned'}</TableCell>
                                        <TableCell>{group.student_count}</TableCell>
                                        <TableCell>
                                            {group.cte_class && (
                                                <Typography variant="caption" display="block">
                                                    CTE: {group.cte_class}
                                                </Typography>
                                            )}
                                            {group.cte_level && (
                                                <Typography variant="caption" display="block">
                                                    Level: {group.cte_level}
                                                </Typography>
                                            )}
                                            {group.core_class && (
                                                <Typography variant="caption" display="block">
                                                    Core: {group.core_class}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        );
    };

    return (
        <Box sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
                Group Reports
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {/* Teacher Groups Report */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Groups by Teacher
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Select Teacher</InputLabel>
                                <Select
                                    value={selectedTeacher}
                                    onChange={(e) => setSelectedTeacher(e.target.value)}
                                    label="Select Teacher"
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 300,
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>Select a teacher...</em>
                                    </MenuItem>
                                    {teachers.map((teacher) => (
                                        <MenuItem key={teacher.id} value={teacher.id}>
                                            {teacher.first_name} {teacher.last_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Grade Level</InputLabel>
                                <Select
                                    value={gradeLevel}
                                    onChange={(e) => setGradeLevel(e.target.value)}
                                    label="Grade Level"
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 300,
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>All Grades</em>
                                    </MenuItem>
                                    {teacherGradeLevels.map((grade) => {
                                        // Convert grade number to database ID
                                        const gradeId = grade - 5; // Grade 6 -> ID 1, Grade 7 -> ID 2, etc.
                                        return (
                                            <MenuItem key={gradeId} value={gradeId}>
                                                Grade {grade}
                                            </MenuItem>
                                        );
                                    })}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="contained"
                                onClick={fetchTeacherGroups}
                                disabled={loading || !selectedTeacher}
                                fullWidth
                            >
                                {loading ? <CircularProgress size={24} /> : 'Get Report'}
                            </Button>
                        </Grid>
                    </Grid>
                    {selectedTeacher && teacherGradeLevels.length > 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            Teacher assigned to grades: {teacherGradeLevels.map(g => `Grade ${g}`).join(', ')}
                        </Typography>
                    )}
                    {renderTeacherReport()}
                </CardContent>
            </Card>

            {/* Group Students Report */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Students by Group
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                                <InputLabel>Select Group</InputLabel>
                                <Select
                                    value={selectedGroup}
                                    onChange={handleGroupChange}
                                    label="Select Group"
                                    renderValue={(selected) => getGroupDisplayName(selected)}
                                    MenuProps={{
                                        PaperProps: {
                                            style: {
                                                maxHeight: 300,
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">
                                        <em>Select a group...</em>
                                    </MenuItem>
                                    {groups.map((group) => (
                                        <MenuItem 
                                            key={`${group.type}-${group.id}`} 
                                            value={group.id}
                                        >
                                            {group.name} ({group.type})
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="contained"
                                onClick={fetchGroupStudents}
                                disabled={loading || !selectedGroup}
                                fullWidth
                            >
                                {loading ? <CircularProgress size={24} /> : 'Get Report'}
                            </Button>
                        </Grid>
                    </Grid>
                    {renderGroupReport()}
                </CardContent>
            </Card>

            {/* All Groups Report */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        All Groups Report
                    </Typography>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Group Type</InputLabel>
                                <Select
                                    value={groupType}
                                    onChange={(e) => setGroupType(e.target.value)}
                                    label="Group Type"
                                >
                                    <MenuItem value="all">All Types</MenuItem>
                                    <MenuItem value="homeroom">Homeroom</MenuItem>
                                    <MenuItem value="cte">CTE</MenuItem>
                                    <MenuItem value="core">Core Class</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <FormControl fullWidth>
                                <InputLabel>Grade Level</InputLabel>
                                <Select
                                    value={gradeLevel}
                                    onChange={(e) => setGradeLevel(e.target.value)}
                                    label="Grade Level"
                                >
                                    <MenuItem value="">All Grades</MenuItem>
                                    {gradeLevels.map((grade) => (
                                        <MenuItem key={grade.value} value={grade.value}>
                                            {grade.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={2}>
                            <Button
                                variant="contained"
                                onClick={fetchAllGroupsReport}
                                disabled={loading}
                                fullWidth
                            >
                                {loading ? <CircularProgress size={24} /> : 'Get Report'}
                            </Button>
                        </Grid>
                    </Grid>
                    {renderAllGroupsReport()}
                </CardContent>
            </Card>
        </Box>
    );
};

export default GroupReports;
